import { Stack, useTheme } from "@xcorejs/ui";
import { cloneElement, Dispatch, MutableRefObject, ReactElement, useRef, useState } from "react";
import { useMediaQuery } from "hooks/useMediaQuery";
import NavItem, { NavItemProps } from "./NavItem";

interface NavItemsProps {
  children: ReactElement<
    Omit<NavItemProps, "delayedState"> & {
      open: boolean;
      setOpen: Dispatch<boolean>;
      view: "dropdown" | "accordion";
      delayedState: MutableRefObject<NodeJS.Timeout | undefined>;
      isAnythingOpen: boolean;
    },
    typeof NavItem
  >[];
}

/**
 * `NavItems` is a container component that manages the state and behavior
 * of multiple `NavItem` components. It dynamically adjusts their behavior
 * based on screen size, switching between `dropdown` and `accordion` views.
 */
const NavItems = ({ children }: NavItemsProps) => {
  const [state, setState] = useState<number>();
  const delayedState = useRef<NodeJS.Timeout>();
  const canceled = useRef<boolean>(false);
  const theme = useTheme();
  const [active] = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);

  const removeState = () => {
    canceled.current = false;
    setTimeout(() => {
      !canceled.current && setState(undefined);
    }, 400);
  };

  return (
    <Stack
      onMouseLeave={removeState}
      direction={{ _: "column", md: "row" }}
      gap={{ _: "0", md: "1.5rem", lg: "2rem" }}
      pt={{ _: "2rem", md: 0 }}
      flex={{ _: "0 0 auto" }}
    >
      {children.filter(Boolean).map((nav, i) =>
        // Create a new version of nav item so additional props can be injected
        cloneElement(nav, {
          key: i,
          // Show dropdown for large screen and accordion for small screen
          view: !active ? "dropdown" : "accordion",
          setOpen: (s: boolean) => {
            if (s) {
              canceled.current = true;
              setState(i);
            } else {
              canceled.current = false;
              removeState();
            }
          },
          delayedState,
          open: state === i,
          isAnythingOpen: !!state,
        })
      )}
    </Stack>
  );
};

export default NavItems;
